
import React, { useEffect } from "react";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { ToggleDocumentDownloadPack } from "../../../../Component/Api/DocumentUpload/DocumentUploadHelper";
import { ConfirmationDialog } from "../../../../Component/Common/Components/ConfirmationDialog";
import { DataTable } from "../../../../Component/Common/Components/DataTable/DataTable";
import { ColumnSortOption } from "../../../../Component/Model/Dto/ColumnSortOption";
import { DocumentsForDownloadParameters } from "../../../../Component/Model/Dto/Document";
import { EEPlanObjective } from "../../../../Component/Model/Dto/EmploymentEquity/Plan/EEObjective";
import { defaultObject } from "../../../../Component/Model/helpers";
import { EntriesPerPage } from "../../../../Component/Model/Table/Table";
import { ScorecardAtom } from "../../../../State/Scorecard.Atom";
import { SupportingDocuments } from "../../../Common/SupportingDocuments/SupportingDocuments";
import { EEPlanObjectiveModal } from "./EEPlanObjectiveModal";
import { EEPlanObjectivesTableMapper } from "./EEPlanObjectivesTableColumn";
import { ObjectivesService } from "../../../../Component/Api/EmploymentEquity/Plan/Objectives/ObjectivesService";
import { ConsolidationAtom } from "../../../../State/Consolidation.Atom";
import { UserActivityService } from "../../../../Component/Api/UserActivity/UserActivityService";
import { ObjectivesSection } from "../../../../Component/Common/Constants/UserActivityTrackingSections";
import { UserActivityActionType } from "../../../../Component/Model/Enum/BEE123Enums";

export const EEPlanObjectives = () => {
    const [previewPopup, setPreviewPopup] = React.useState<boolean>(false);
    const [documentsPopUp, setDocumentsPopUp] = React.useState<boolean>(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = React.useState<boolean>(false);
    const [objectivesData, setObjectivesData] = React.useState<EEPlanObjective.DtoIn[]>(defaultObject);
    const [entriesPerPage, setEntriesPerPage] = React.useState<EntriesPerPage>({ defaultValue: 10 });
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [filteredItemsCount, setFilteredItemsCount] = React.useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [selectedObjectiveId, setSelectedObjectiveId] = React.useState<string>("");
    const [selectedDocumentId, setSelectedDocumentId] = React.useState<string>("");
    const [columnSortOptions, setColumnSortOptions] = React.useState<ColumnSortOption[]>([]);
    const scorecard = useRecoilValue(ScorecardAtom);
    const isConsolidatedScorecard = useRecoilValue(ConsolidationAtom);

    const link = 'EmploymentEquityObjectives';
    const scorecardId = scorecard.scorecardId;
    const confirmDeleteMessage = "This action cannot be reversed. Are you sure you want to delete the objective and all the associated notes and supporting documents?"


    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const handleCloseForm = () => {
        setPreviewPopup(false);
        setSelectedObjectiveId("");
    }

    const handleCloseConfirmation = () => {
        setShowConfirmationDialog(false);
        setSelectedObjectiveId("");
    }

    const handleEdit = (id: string) => {
        setSelectedObjectiveId(id);
        setPreviewPopup(true);
    }

    const handleConfirmDelete = (id: string) => {
        setSelectedObjectiveId(id);
        setShowConfirmationDialog(true);
    }

    const objectivesLog = (actionType: number, actionDescription: string) => {
        UserActivityService.createActivityLog({
            section: ObjectivesSection,
            actionType: actionType,
            actionDescription: actionDescription,
            scorecardId: scorecardId,
            requestUrl: window.location.href
        });
    } 

    const handleDelete = async () => {

        try {
            await ObjectivesService.DeleteObjective(selectedObjectiveId);
            objectivesLog(UserActivityActionType.Delete, "Delete an objective")
            toast.success("Objective deleted succesfully")
            fetchData();

        } catch (error) {
            console.log(error)
        }
        handleCloseConfirmation();

    }

    const handleAttachedSD = (id: string) => {
        setSelectedDocumentId(id);
        setDocumentsPopUp(true);
    }

    const handleToggleDocumentPack = async (id: string) => {

        let added = objectivesData.find(objective => objective.id === id)?.documentsSelectedForDownload;
        setSelectedObjectiveId(id);
        if (added !== undefined) {

            const toggleDocumentsParamters: DocumentsForDownloadParameters = {
                entityId: id,
                scorecardId: scorecardId,
                link: link
            };

            await ToggleDocumentDownloadPack(toggleDocumentsParamters, added);
            fetchData();
        }

    }

    const onDocumentPopupClose = () => {
        setDocumentsPopUp(false);
        fetchData();
    }

    const objectivesSearchRequest: EEPlanObjective.SearchRequest = {
        pageNumber: pageNumber as number,
        pageSize: entriesPerPage.defaultValue as number,
        scorecardId: scorecardId,
        columnSortOptions: columnSortOptions
    };

    const fetchData = async () => {
        setIsLoading(true);
        try {
            let searchResult = await ObjectivesService.SearchObjectives(objectivesSearchRequest);
            if (searchResult.data) {
                setObjectivesData(searchResult.data.results);
                setFilteredItemsCount(searchResult.data.filteredItemsCount);
                setTotalItemsCount(searchResult.data.totalItemsCount);

            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }

    };

    useEffect(() => {
        fetchData();
        objectivesLog(UserActivityActionType.View, "View list of objectives")
    }, [entriesPerPage, pageNumber, columnSortOptions]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAddObjective = () => {
        setPreviewPopup(true)
    }
    const bulkActions = [
        {
            label: "Add Objective",
            action: handleAddObjective
        }
    ]

    const objectivesTableData = EEPlanObjectivesTableMapper(objectivesData, handleEdit, handleConfirmDelete, handleAttachedSD, isConsolidatedScorecard);

    return (
        <>
            <DataTable
                key={"ObjectivesTable"}
                table={objectivesTableData}
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemsCount}
                totalItemsCount={totalItemsCount}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
                isLoading={isLoading}
                bulkActions={bulkActions}
                manualSorting={true}
                columnSortingChanged={columnSortingChanged}
            ></DataTable>

            {previewPopup && <EEPlanObjectiveModal previewPopup={previewPopup} id={selectedObjectiveId !== "" ? selectedObjectiveId : undefined} link={link} scorecardId={scorecardId} handleCancel={handleCloseForm} refreshTable={fetchData} setObjectiveId={setSelectedObjectiveId} />}

            {documentsPopUp && <SupportingDocuments display={documentsPopUp} handleCancel={() => onDocumentPopupClose()} link={link} id={selectedDocumentId} 
                scorecardId={scorecardId} isEEModule={true} section={ObjectivesSection}/>}

            <ConfirmationDialog
                showDialog={showConfirmationDialog}
                handleAccept={handleDelete}
                handleCancel={handleCloseConfirmation}
                heading={"Confirm Delete"}
                message={confirmDeleteMessage}
            />
        </>
    )
}